// usePermission(permission, scope) — deriva de `/me` (Parte 14.2): `networkAccess.roles[]` no escopo de rede,
// `contexts[].effectiveRoles[]` do tenant ativo no escopo de tenant. Sem papel resolvido → sempre `false`.
import type { MeContext, MeResponse } from '@giyus/api-client';
import { useMemo } from 'react';

import { useMe } from '../auth/authContext';
import { isSuperAdminUser } from '../auth/greenfield';
import { browserPreferenceStorage, readContextPreference } from '../context/contextPreferences';
import { findContext, resolveEffectiveScope } from '../context/contextResolver';
import { useContextStore } from '../context/contextStore';
import {
  ALL_FULL_ADMIN_PERMISSIONS,
  type Permission,
  networkRolePermissions,
  tenantRolePermissions,
} from './rolePermissions';

export type PermissionScope = 'network' | 'tenant';

export interface PermissionInput {
  readonly me: Pick<MeResponse, 'networkAccess' | 'contexts'> & {
    readonly user?: {
      readonly id?: string;
      readonly email?: string;
      readonly displayName?: string;
    };
  };
  readonly scope: PermissionScope;
  /** Contexto de tenant ativo (obrigatório para `scope: 'tenant'`). */
  readonly context?: MeContext | null;
}

/** Conjunto de permissões efetivas no escopo pedido (função pura, testável). */
export function effectivePermissions(input: PermissionInput): ReadonlySet<Permission> {
  if (isSuperAdminUser(input.me)) {
    return new Set(ALL_FULL_ADMIN_PERMISSIONS);
  }
  const result = new Set<Permission>();
  if (input.scope === 'network') {
    if (!input.me.networkAccess.hasAccess) {
      return result;
    }
    for (const role of input.me.networkAccess.roles) {
      for (const permission of networkRolePermissions(role)) {
        result.add(permission);
      }
    }
    return result;
  }
  const context = input.context;
  if (!context) {
    return result;
  }
  // TODO(GIY-050): permissões de classe S valem só nos estudos delegados; no M0 o papel do tenant basta para a navegação.
  for (const effective of context.effectiveRoles) {
    for (const permission of tenantRolePermissions(effective.role)) {
      result.add(permission);
    }
  }
  return result;
}

export function hasPermission(input: PermissionInput, permission: Permission): boolean {
  return effectivePermissions(input).has(permission);
}

/** Hook do shell: escopo padrão = o da rota ativa (rede ou tenant), com fallback para o escopo padrão do usuário em `/me/*`. */
export function usePermissions(scope?: PermissionScope): ReadonlySet<Permission> {
  const me = useMe();
  const storeIsNetworkScope = useContextStore((s) => s.isNetworkScope);
  const storeActiveTenantCode = useContextStore((s) => s.activeTenantCode);
  const effective = useMemo(() => {
    const remembered = readContextPreference(browserPreferenceStorage(), me.user.id);
    return resolveEffectiveScope(me, storeIsNetworkScope, storeActiveTenantCode, remembered);
  }, [me, storeIsNetworkScope, storeActiveTenantCode]);

  const effectiveScope: PermissionScope = scope ?? (effective.isNetworkScope ? 'network' : 'tenant');
  const effectiveTenantCode = storeActiveTenantCode ?? effective.activeTenantCode;
  return useMemo(
    () =>
      effectivePermissions({
        me,
        scope: effectiveScope,
        context:
          effectiveScope === 'tenant' ? (findContext(me, effectiveTenantCode) ?? null) : null,
      }),
    [me, effectiveScope, effectiveTenantCode],
  );
}

export function usePermission(permission: Permission, scope?: PermissionScope): boolean {
  return usePermissions(scope).has(permission);
}
